import type {NexusEdge, NexusGraphResponse, NexusNode} from 'src/api/ProactApiClient';
import { NexusNodeType} from 'src/api/ProactApiClient';

export type SummarySegment = {text: string; emphasis?: boolean};

/**
 * Sentence builder for each NexusNodeType.
 * Entity names are wrapped in ** markers for emphasis in rich rendering.
 * isChild indicates the node has an incoming edge (follows a parent in the causal chain).
 */
const sentenceBuilders: Record<NexusNodeType, (node: NexusNode, isChild: boolean) => string> = {
  [NexusNodeType.Form]: (node) => {
    const name = node.name ?? 'A form';
    const parts = [`**${name}** was submitted`];
    if (node.submittedByName) parts.push(`by ${node.submittedByName}`);
    if (node.submittedAt) parts.push(`on ${new Date(node.submittedAt).toLocaleDateString()}`);
    return parts.join(' ') + '.';
  },

  [NexusNodeType.Workflow]: (node, isChild) => {
    const name = node.definitionName;
    if (node.state === 'Completed' || node.completedAt) {
      return name
        ? `The workflow **${name}** has completed.`
        : 'The workflow has completed.';
    }
    if (isChild) {
      return name
        ? `This triggered the workflow **${name}**.`
        : 'This triggered the workflow.';
    }
    return name
      ? `The workflow **${name}** was triggered.`
      : 'The workflow was triggered.';
  },

  [NexusNodeType.Task]: (node) => {
    const name = node.subject;
    if (node.state === 'Completed') {
      const label = name ? `The task **${name}**` : 'The task';
      const parts = [`${label} was completed`];
      if (node.assigneeName) parts.push(`by ${node.assigneeName}`);
      return parts.join(' ') + '.';
    }
    const label = name ? `A task **${name}**` : 'A task';
    const stateNote = node.state ? ` (${node.state.toLowerCase()})` : '';
    if (node.assigneeName) {
      return `${label} was assigned to ${node.assigneeName}${stateNote}.`;
    }
    return `${label} was created${stateNote}.`;
  },

  [NexusNodeType.Email]: (node) => {
    const label = node.subject ? `An email **${node.subject}**` : 'An email';
    if (node.state === 'Skipped') {
      return `${label} was skipped.`;
    }
    if (node.state && node.state !== 'Completed') {
      return `${label} is pending.`;
    }
    const parts = [`${label} was sent`];
    if (node.recipientCount) {
      parts.push(`to ${node.recipientCount} ${node.recipientCount === 1 ? 'recipient' : 'recipients'}`);
    }
    return parts.join(' ') + '.';
  },

  [NexusNodeType.TrainingSession]: (node) => {
    const name = node.courseName;
    if (node.state === 'Completed') {
      const label = name ? `**${name}** training` : 'Training';
      const parts = [`${label} was completed`];
      if (node.traineeName) parts.push(`by ${node.traineeName}`);
      return parts.join(' ') + '.';
    }
    const label = name ? `**${name}** training` : 'Training';
    const parts = [`${label} was assigned`];
    if (node.traineeName) parts.push(`to ${node.traineeName}`);
    if (node.state && node.state.toLowerCase() !== 'assigned') {
      return parts.join(' ') + ` (${node.state.toLowerCase()}).`;
    }
    return parts.join(' ') + '.';
  },

  [NexusNodeType.ItemAssignment]: (node) => {
    const name = node.itemName ?? 'An item';
    const parts = [`**${name}** was assigned`];
    if (node.assigneeName) parts.push(`to ${node.assigneeName}`);
    return parts.join(' ') + '.';
  },

  [NexusNodeType.Schedule]: (node) => {
    const interval = node.intervalType ? node.intervalType.toLowerCase() : 'recurring';
    if (node.nextProcessDate) {
      return `A ${interval} schedule is set to trigger next on ${new Date(node.nextProcessDate).toLocaleDateString()}.`;
    }
    return `A ${interval} schedule is configured.`;
  },

  [NexusNodeType.BadgeExtension]: (node) => {
    if (node.state === 'Skipped') {
      return node.badgeExtensionDays
        ? `A ${node.badgeExtensionDays}-day badge extension was skipped.`
        : 'The badge extension was skipped.';
    }
    if (node.state && node.state !== 'Completed') {
      return node.badgeExtensionDays
        ? `A ${node.badgeExtensionDays}-day badge extension is pending.`
        : 'A badge extension is pending.';
    }
    if (node.badgeExtensionDays) {
      return `The badge was extended by ${node.badgeExtensionDays} days.`;
    }
    return 'The badge was extended.';
  },

  [NexusNodeType.Unknown]: (node) => {
    const parts = ['The workflow was manually triggered'];
    if (node.triggeredByName) parts.push(`by ${node.triggeredByName}`);
    if (node.triggeredDate) parts.push(`on ${new Date(node.triggeredDate).toLocaleDateString()}`);
    return parts.join(' ') + '.';
  },
};

/**
 * Topologically sort nodes following edge direction (from → to),
 * using createdAt as a tiebreaker for nodes at the same depth.
 */
function topologicalSort(nodes: NexusNode[], edges: Pick<NexusEdge, 'from' | 'to'>[]): NexusNode[] {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const inDegree = new Map<string, number>();
  const adjacency = new Map<string, string[]>();

  for (const node of nodes) {
    inDegree.set(node.id, 0);
    adjacency.set(node.id, []);
  }

  for (const edge of edges) {
    if (nodeMap.has(edge.from) && nodeMap.has(edge.to)) {
      inDegree.set(edge.to, (inDegree.get(edge.to) ?? 0) + 1);
      adjacency.get(edge.from)!.push(edge.to);
    }
  }

  // Start with nodes that have no incoming edges, sorted by createdAt
  const queue: NexusNode[] = nodes
    .filter((n) => (inDegree.get(n.id) ?? 0) === 0)
    .sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return aTime - bTime;
    });

  const result: NexusNode[] = [];

  while (queue.length > 0) {
    // Sort current queue by createdAt for deterministic tiebreaking
    queue.sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return aTime - bTime;
    });

    const current = queue.shift()!;
    result.push(current);

    for (const neighbor of adjacency.get(current.id) ?? []) {
      const newDegree = (inDegree.get(neighbor) ?? 1) - 1;
      inDegree.set(neighbor, newDegree);
      if (newDegree === 0) {
        queue.push(nodeMap.get(neighbor)!);
      }
    }
  }

  return result;
}

/**
 * Join a list of names with commas and "and" (e.g., "A, B, and C").
 */
function joinNames(names: string[]): string {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return names.slice(0, -1).join(', ') + ', and ' + names[names.length - 1];
}

/**
 * Short label for a node, used in phase group sentences.
 */
function nodeLabel(node: NexusNode): string {
  switch (node.type) {
    case NexusNodeType.Task:
      return node.subject ? `**${node.subject}**` : 'a task';
    case NexusNodeType.Email:
      return node.subject ? `**${node.subject}**` : 'an email';
    case NexusNodeType.BadgeExtension:
      return 'badge extension';
    default:
      return node.name ?? node.subject ?? 'an action';
  }
}

/**
 * Represents a group of nodes within a mutually-exclusive workflow phase.
 */
interface PhaseGroup {
  phaseName: string;
  nodeIds: Set<string>;
}

/**
 * Build the raw summary string with ** markers around entity names.
 */
function buildRawSummary(response: NexusGraphResponse): string {
  if (!response.nodes.length) return '';

  const nodeMap = new Map(response.nodes.map((n) => [n.id, n]));
  const nodeIds = new Set(response.nodes.map((n) => n.id));

  // Build parent→children map and track which nodes are children
  const childIds = new Set<string>();
  const childrenOf = new Map<string, string[]>();
  for (const edge of response.edges) {
    if (nodeIds.has(edge.from) && nodeIds.has(edge.to)) {
      childIds.add(edge.to);
      if (!childrenOf.has(edge.from)) childrenOf.set(edge.from, []);
      childrenOf.get(edge.from)!.push(edge.to);
    }
  }

  // Workflows covered by a multi-trigger summary use standalone phrasing
  const multiTriggerWorkflows = new Set<string>();
  for (const [, children] of childrenOf) {
    const wfChildren = children.filter((id) => nodeMap.get(id)?.type === NexusNodeType.Workflow);
    if (wfChildren.length >= 2) {
      wfChildren.forEach((id) => multiTriggerWorkflows.add(id));
    }
  }

  // Identify mutually-exclusive phase groups:
  // edges from the same parent with the same phaseName and mutuallyExclusiveCompletion
  const phaseGroupOf = new Map<string, PhaseGroup>(); // nodeId → group
  const phaseGroupsByParent = new Map<string, Map<string, PhaseGroup>>(); // parentId → phaseName → group
  for (const edge of response.edges) {
    if (!edge.phaseName || !edge.mutuallyExclusiveCompletion) continue;
    if (!nodeIds.has(edge.from) || !nodeIds.has(edge.to)) continue;

    if (!phaseGroupsByParent.has(edge.from)) phaseGroupsByParent.set(edge.from, new Map());
    const parentGroups = phaseGroupsByParent.get(edge.from)!;
    if (!parentGroups.has(edge.phaseName)) {
      parentGroups.set(edge.phaseName, {phaseName: edge.phaseName, nodeIds: new Set()});
    }
    const group = parentGroups.get(edge.phaseName)!;
    group.nodeIds.add(edge.to);
    phaseGroupOf.set(edge.to, group);
  }

  // Only keep groups with 2+ members (a single node isn't a meaningful group)
  for (const [nodeId, group] of phaseGroupOf) {
    if (group.nodeIds.size < 2) phaseGroupOf.delete(nodeId);
  }

  const sorted = topologicalSort(response.nodes, response.edges);
  const sentences: string[] = [];
  const emittedPhaseGroups = new Set<PhaseGroup>();

  for (const node of sorted) {
    // If this node belongs to a mutually-exclusive phase group, emit a group sentence once
    const group = phaseGroupOf.get(node.id);
    if (group && !emittedPhaseGroups.has(group)) {
      emittedPhaseGroups.add(group);
      const groupNodes = [...group.nodeIds].map((id) => nodeMap.get(id)!);
      const completed = groupNodes.filter((n) => n.state === 'Completed');
      const skipped = groupNodes.filter((n) => n.state !== 'Completed');

      const parts: string[] = [`In the **${group.phaseName}** phase`];
      if (completed.length > 0) {
        const completedLabels = completed.map((n) => nodeLabel(n));
        parts.push(`${joinNames(completedLabels)} was completed`);
        if (skipped.length > 0) {
          const skippedLabels = skipped.map((n) => nodeLabel(n));
          parts.push(`and ${joinNames(skippedLabels)} ${skipped.length === 1 ? 'was' : 'were'} skipped`);
        }
      } else {
        // No completions yet — list all as pending
        const allLabels = groupNodes.map((n) => nodeLabel(n));
        const stateNote = groupNodes[0].state ? ` (${groupNodes[0].state.toLowerCase()})` : '';
        parts.push(`${joinNames(allLabels)} ${groupNodes.length === 1 ? 'is' : 'are'} pending${stateNote}`);
      }
      sentences.push(parts.join(', ') + '.');
      continue;
    }

    // Skip nodes already covered by a phase group
    if (group) continue;

    const builder = sentenceBuilders[node.type];
    if (!builder) continue;
    const isChild = childIds.has(node.id);
    const useConnective = isChild && !multiTriggerWorkflows.has(node.id);
    const sentence = builder(node, useConnective);
    if (sentence) sentences.push(sentence);

    // After a node with 2+ workflow children, add a trigger summary
    const children = childrenOf.get(node.id) ?? [];
    const wfChildren = children
      .map((id) => nodeMap.get(id))
      .filter((n): n is NexusNode => !!n && n.type === NexusNodeType.Workflow);
    if (wfChildren.length >= 2) {
      const names = wfChildren.map((w) =>
        w.definitionName ? `**${w.definitionName}**` : 'a workflow'
      );
      sentences.push(`This triggered the workflows ${joinNames(names)}.`);
    }
  }

  if (response.truncated) {
    sentences.push('Additional steps may exist beyond this summary.');
  }

  return sentences.join(' ');
}

/**
 * Generate a plain-text summary (strips emphasis markers).
 * Used for tests and non-rich contexts.
 */
export function generateNexusSummary(response: NexusGraphResponse): string {
  return buildRawSummary(response).replace(/\*\*/g, '');
}

/**
 * Generate a structured summary with emphasis segments for rich rendering.
 * Entity names are marked with { emphasis: true } for visual differentiation.
 */
export function generateNexusSummarySegments(response: NexusGraphResponse): SummarySegment[] {
  const raw = buildRawSummary(response);
  if (!raw) return [];

  const parts = raw.split(/(\*\*.*?\*\*)/);
  return parts.filter(Boolean).map((part) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return {text: part.slice(2, -2), emphasis: true};
    }
    return {text: part};
  });
}
